// src/components/exam/ProvasSimulados.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Award, SlidersHorizontal,
    Play, Trash2, Clock, Eye, Building2, Calendar, FileText,
    CheckCircle2, RotateCcw, ChevronDown, Lock, FlaskConical,
} from 'lucide-react';
import { examService } from '../../services/examService';
import { simuladoService } from '../../services/simuladoService';
import { questionService } from '../../services/questionService';
import { OfficialExam, UserExamAttempt } from '../../types/exam.types';
import { Simulado } from '../../types/simulado.types';
import './ProvasSimulados.css';

// ─────────────────────────────────────────────────────────────
export type SectionState = 'provas-catalogo' | 'provas-historico' | 'simulados-criar' | 'simulados-historico';

interface ProvasSimuladosProps {
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
    onSectionChange: (section: SectionState) => void;
}

// ── Flatten de árvore de categorias ──────────────────────────
interface CategoryNode { _id: string; name: string; level: number; questionsCount: number; children: CategoryNode[]; }
type FlatCat = { _id: string; name: string; level: number; parentId: string | null; questionsCount: number };
function flattenTree(nodes: CategoryNode[], parentId: string | null = null): FlatCat[] {
    const result: FlatCat[] = [];
    for (const node of nodes) {
        result.push({ _id: node._id, name: node.name, level: node.level, parentId, questionsCount: node.questionsCount });
        if (node.children?.length) result.push(...flattenTree(node.children, node._id));
    }
    return result;
}

const DIFFICULTY_OPTIONS = [
    { value: 'facil',   label: 'Fácil',   color: '#5FBA61' },
    { value: 'medio',   label: 'Médio',   color: '#D4B35D' },
    { value: 'dificil', label: 'Difícil', color: '#EF4444' },
];

// ── Separador de instituição (catálogo) ───────────────────────
const ExamSectionSeparator: React.FC<{ name: string }> = ({ name }) => (
    <div className="exam-sep">
        <div className="exam-sep-line exam-sep-line--left" />
        <div className="exam-sep-content">
            <span className="exam-sep-name">{name}</span>
        </div>
        <div className="exam-sep-line exam-sep-line--right" />
    </div>
);

// ── Catálogo de Provas ────────────────────────────────────────
const ProvasCatalogView: React.FC<{
    exams: OfficialExam[];
    attempts: UserExamAttempt[];
    loading: boolean;
}> = ({ exams, attempts, loading }) => {
    const [filters, setFilters] = useState<{ institution: string; year: number | undefined; area: string }>({
        institution: '', year: undefined, area: ''
    });

    const uniqueInstitutions = [...new Set(exams.map(e => e.institution).filter(Boolean))] as string[];
    const uniqueYears = [...new Set(exams.map(e => e.year).filter(Boolean))].sort((a, b) => (b as number) - (a as number));
    const uniqueAreas = [...new Set(exams.map(e => e.area).filter((a): a is string => Boolean(a)))];

    const filteredExams = exams.filter(e => {
        if (filters.institution && e.institution !== filters.institution) return false;
        if (filters.year && e.year !== filters.year) return false;
        if (filters.area && e.area !== filters.area) return false;
        return true;
    });

    // Agrupar por institution
    const institutionGroups = (() => {
        const withInst: Record<string, OfficialExam[]> = {};
        const without: OfficialExam[] = [];
        for (const exam of filteredExams) {
            if (exam.institution) {
                if (!withInst[exam.institution]) withInst[exam.institution] = [];
                withInst[exam.institution].push(exam);
            } else {
                without.push(exam);
            }
        }
        const sortedNames = Object.keys(withInst).sort();
        const result: { name: string | null; exams: OfficialExam[] }[] = sortedNames.map(n => ({ name: n, exams: withInst[n] }));
        if (without.length > 0) result.push({ name: null, exams: without });
        return result;
    })();

    if (loading) return <div className="ps-loading"><div className="ps-spinner" /><p>Carregando provas...</p></div>;

    return (
        <>
            <div className="ps-filters">
                <div className="ps-filter-group">
                    <label className="ps-filter-label">Prova</label>
                    <select className="ps-filter-select" value={filters.institution} onChange={e => setFilters({ ...filters, institution: e.target.value })}>
                        <option value="">Todas</option>
                        {uniqueInstitutions.map(i => <option key={i} value={i}>{i}</option>)}
                    </select>
                </div>
                <div className="ps-filter-group">
                    <label className="ps-filter-label">Ano</label>
                    <select className="ps-filter-select" value={filters.year || ''} onChange={e => setFilters({ ...filters, year: e.target.value ? Number(e.target.value) : undefined })}>
                        <option value="">Todos</option>
                        {uniqueYears.map(y => <option key={y as number} value={y as number}>{y}</option>)}
                    </select>
                </div>
                <div className="ps-filter-group">
                    <label className="ps-filter-label">Área</label>
                    <select className="ps-filter-select" value={filters.area} onChange={e => setFilters({ ...filters, area: e.target.value })}>
                        <option value="">Todas</option>
                        {uniqueAreas.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                </div>
                {(filters.institution || filters.year || filters.area) && (
                    <button className="ps-filter-clear" onClick={() => setFilters({ institution: '', year: undefined, area: '' })}>
                        Limpar filtros
                    </button>
                )}
            </div>

            {institutionGroups.length === 0 ? (
                <div className="ps-empty">
                    <FileText size={56} />
                    <h3>Nenhuma prova disponível</h3>
                    <p>Aguarde novas provas serem adicionadas</p>
                </div>
            ) : (
                <div className="exams-sections">
                    {institutionGroups.map((group) => (
                        <div key={group.name ?? '__outros'} className="exams-section">
                            <ExamSectionSeparator name={group.name ?? 'Outras provas'} />
                            <div className="exams-grid">
                                {group.exams.map(exam => {
                                    const userAttempt = attempts.find(a => a.examId === exam._id);
                                    return (
                                        <div key={exam._id} className="exam-card">
                                            <div className="exam-header">
                                                <h3>{exam.name}</h3>
                                                {exam.year && <span className="exam-year">{exam.year}</span>}
                                            </div>
                                            <div className="exam-info">
                                                {exam.area && <div className="info-row"><FileText size={14} /><span>{exam.area}</span></div>}
                                                <div className="info-row"><Award size={14} /><span>{exam.totalQuestions} questões</span></div>
                                                <div className="info-row"><Clock size={14} /><span>{exam.officialDuration} min</span></div>
                                            </div>
                                            {userAttempt?.status === 'finalizado' && (
                                                <div className="exam-result">
                                                    <div className="result-badge">Nota: {userAttempt.results.accuracy}%</div>
                                                    <div className="result-details">{userAttempt.results.totalCorrect}/{exam.totalQuestions} acertos</div>
                                                </div>
                                            )}
                                            <div className="exam-actions">
                                                {!userAttempt || userAttempt.status !== 'finalizado' ? (
                                                    <button className="btn-start-exam" onClick={() => window.open(`/prova/${exam._id}`, '_blank')}>
                                                        <Play size={14} /> Fazer Prova
                                                    </button>
                                                ) : (
                                                    <button className="btn-view-result" onClick={() => window.open(`/prova/${exam._id}`, '_blank')}>
                                                        <Eye size={14} /> Ver Resultado
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};

// ── Histórico de Provas ───────────────────────────────────────
const ProvasHistoricoView: React.FC<{
    finishedAttempts: UserExamAttempt[];
    exams: OfficialExam[];
    loading: boolean;
    onDeleteAttempt: (id: string) => void;
}> = ({ finishedAttempts, exams, loading, onDeleteAttempt }) => {
    const [search, setSearch] = useState('');

    const formatDate = (d: string) => d ? new Date(d).toLocaleDateString('pt-BR') : '—';
    const formatTime = (seconds: number) => {
        if (!seconds) return '0:00';
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        return h > 0 ? `${h}h${String(m).padStart(2, '0')}min` : `${m}:${String(s).padStart(2, '0')}`;
    };

    const filtered = finishedAttempts.filter(attempt => {
        const examIdStr = typeof attempt.examId === 'object'
            ? (attempt.examId as any)?._id ?? attempt.examId
            : attempt.examId;
        const exam = exams.find(e => e._id === String(examIdStr));
        return (exam?.name ?? '').toLowerCase().includes(search.toLowerCase());
    });

    if (loading) return <div className="ps-loading"><div className="ps-spinner" /><p>Carregando histórico...</p></div>;

    return (
        <>
            {finishedAttempts.length > 0 && (
                <div className="hist__search-bar">
                    <input
                        type="text"
                        placeholder="Buscar prova pelo nome..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="hist__search-input"
                    />
                </div>
            )}
            {filtered.length === 0 ? (
                <div className="ps-empty">
                    <Award size={56} />
                    <h3>{finishedAttempts.length === 0 ? 'Nenhuma prova realizada' : 'Nenhuma prova encontrada'}</h3>
                    <p>{finishedAttempts.length === 0 ? 'Comece fazendo uma prova no Catálogo' : 'Tente outro termo de busca'}</p>
                </div>
            ) : (
                <div className="hist__grid">
                    {filtered.map(attempt => {
                        const examIdStr = typeof attempt.examId === 'object'
                            ? (attempt.examId as any)?._id ?? attempt.examId
                            : attempt.examId;
                        const exam = exams.find(e => e._id === String(examIdStr));
                        return (
                            <div key={attempt._id} className="hist__card">
                                <div className="hist__card-top">
                                    <div className="hist__card-info">
                                        <h3 className="hist__card-title">{exam?.name ?? 'Prova'}</h3>
                                        <div className="hist__card-meta"><Calendar size={12} /><span>{formatDate(attempt.finishedAt || attempt.startedAt)}</span></div>
                                        {exam?.institution && (
                                            <div className="hist__card-meta"><Building2 size={12} /><span>{exam.institution}{exam.year ? ` · ${exam.year}` : ''}</span></div>
                                        )}
                                    </div>
                                    <span className="hist__badge"><CheckCircle2 size={12} /> Concluída</span>
                                </div>
                                <div className="hist__stats">
                                    <div className="hist__stat"><span className="hist__stat-label">Nota</span><span className="hist__stat-value hist__stat-value--ok">{attempt.results.accuracy}%</span></div>
                                    <div className="hist__stat"><span className="hist__stat-label">Acertos</span><span className="hist__stat-value">{attempt.results.totalCorrect}</span></div>
                                    <div className="hist__stat"><span className="hist__stat-label">Erros</span><span className="hist__stat-value hist__stat-value--err">{attempt.results.totalWrong}</span></div>
                                    <div className="hist__stat"><span className="hist__stat-label">Tempo</span><span className="hist__stat-value">{formatTime(attempt.results.totalTime)}</span></div>
                                </div>
                                <div className="hist__actions">
                                    <button className="hist__btn hist__btn--view" onClick={() => {
                                        const eid = typeof attempt.examId === 'object' ? (attempt.examId as any)?._id ?? attempt.examId : attempt.examId;
                                        window.open(`/prova/${eid}`, '_blank');
                                    }}>
                                        <Eye size={14} /> Ver Resultado
                                    </button>
                                    {exam && (
                                        <button className="hist__btn hist__btn--retry" onClick={() => window.open(`/prova/${exam._id}`, '_blank')}>
                                            <RotateCcw size={14} /> Refazer
                                        </button>
                                    )}
                                    <button className="hist__btn hist__btn--delete" onClick={() => onDeleteAttempt(attempt._id)} title="Remover do histórico">
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </>
    );
};

// ── Criar Simulado ────────────────────────────────────────────
const SimuladosCriarView: React.FC<{
    onShowToast: (msg: string, type: 'success' | 'error' | 'info') => void;
}> = ({ onShowToast }) => {
    const navigate = useNavigate();
    const [allCategories, setAllCategories] = useState<FlatCat[]>([]);

    const [name, setName]             = useState('');
    const [level1Id, setLevel1Id]     = useState('');
    const [level2Id, setLevel2Id]     = useState('');
    const [level3Id, setLevel3Id]     = useState('');
    const [level4Id, setLevel4Id]     = useState('');
    const [difficulty, setDifficulty] = useState('');
    const [qty, setQty]               = useState(20);

    const [questionCount, setQuestionCount] = useState<number | null>(null);
    const [countLoading, setCountLoading]   = useState(false);
    const [creating, setCreating]           = useState(false);

    useEffect(() => {
        questionService.getCategoriesTree()
            .then(tree => setAllCategories(flattenTree(tree as CategoryNode[])))
            .catch(() => {});
    }, []);

    const level1Opts = allCategories.filter(c => c.level === 1 && c.questionsCount > 0);
    const level2Opts = allCategories.filter(c => c.level === 2 && c.questionsCount > 0 && (!level1Id || c.parentId === level1Id));
    const level3Opts = allCategories.filter(c => c.level === 3 && c.questionsCount > 0 && (!level2Id || c.parentId === level2Id));
    const level4Opts = allCategories.filter(c => c.level === 4 && c.questionsCount > 0 && (!level3Id || c.parentId === level3Id));

    useEffect(() => {
        const timer = setTimeout(async () => {
            setCountLoading(true);
            try {
                const count = await questionService.countQuestions({
                    level1Id: level1Id || undefined,
                    level2Id: level2Id || undefined,
                    level3Id: level3Id || undefined,
                    level4Id: level4Id || undefined,
                    difficulty: (difficulty as any) || undefined,
                });
                setQuestionCount(count);
            } catch { setQuestionCount(null); }
            finally { setCountLoading(false); }
        }, 350);
        return () => clearTimeout(timer);
    }, [level1Id, level2Id, level3Id, level4Id, difficulty]);

    const getName = (id: string) => allCategories.find(c => c._id === id)?.name ?? '';

    const handleCreate = async () => {
        try {
            setCreating(true);
            const categories: Record<string, string> = {};
            if (level1Id) categories.level1 = getName(level1Id);
            if (level2Id) categories.level2 = getName(level2Id);
            if (level3Id) categories.level3 = getName(level3Id);
            if (level4Id) categories.level4 = getName(level4Id);

            const simuladoName = name.trim() || `Simulado — ${new Date().toLocaleDateString('pt-BR')}`;
            const simulado = await simuladoService.createSimulado({
                name: simuladoName,
                source: 'banco',
                config: {
                    categories: categories as any,
                    difficulty: (difficulty || 'all') as any,
                    totalQuestions: qty,
                    timeLimit: null,
                }
            });
            onShowToast('Simulado criado! Boa prova.', 'success');
            navigate(`/simulado/${simulado._id}`);
        } catch (e: any) {
            onShowToast(e.message || 'Erro ao criar simulado', 'error');
        } finally {
            setCreating(false);
        }
    };

    const effectiveQty = questionCount !== null ? Math.min(qty, questionCount) : qty;
    const canCreate = !creating && questionCount !== null && questionCount > 0;

    const diffLabel = difficulty
        ? DIFFICULTY_OPTIONS.find(o => o.value === difficulty)?.label
        : 'Todas';
    const diffColor = difficulty
        ? DIFFICULTY_OPTIONS.find(o => o.value === difficulty)?.color
        : undefined;
    const summaryName = name.trim() || `Simulado — ${new Date().toLocaleDateString('pt-BR')}`;

    return (
        <div className="psc-layout">

            {/* ── Painel esquerdo: controles ─────────────────────── */}
            <div className="psc-form-panel">

                <p className="psc-hint">
                    Todos os filtros são opcionais — deixe em branco para usar o banco completo.
                </p>

                {/* Nome */}
                <div className="psc-field">
                    <label className="psc-label">Nome <span className="psc-optional">(opcional)</span></label>
                    <input
                        type="text"
                        className="psc-input"
                        placeholder="Ex: Simulado de Física — sem nome usa a data"
                        value={name}
                        onChange={e => setName(e.target.value)}
                    />
                </div>

                {/* Conteúdo — grade 2×2 */}
                <div className="psc-group">
                    <div className="psc-section-label">Conteúdo</div>
                    <div className="psc-hierarchy-2x2">

                        <div className="psc-select-group">
                            <label className="psc-label">Campo de Estudo</label>
                            <div className="psc-select-wrap">
                                <select className={`psc-select ${level1Id ? 'psc-select--active' : ''}`} value={level1Id}
                                    onChange={e => { setLevel1Id(e.target.value); setLevel2Id(''); setLevel3Id(''); setLevel4Id(''); }}>
                                    <option value="">Todos</option>
                                    {level1Opts.map(c => <option key={c._id} value={c._id}>{c.name} ({c.questionsCount})</option>)}
                                </select>
                                <ChevronDown size={13} className="psc-select-icon" />
                            </div>
                        </div>

                        <div className="psc-select-group">
                            <label className="psc-label">Assunto</label>
                            {!level1Id ? (
                                <div className="psc-select-hint"><Lock size={12} /><span>Selecione um campo</span></div>
                            ) : (
                                <div className="psc-select-wrap">
                                    <select className={`psc-select ${level2Id ? 'psc-select--active' : ''}`} value={level2Id}
                                        onChange={e => { setLevel2Id(e.target.value); setLevel3Id(''); setLevel4Id(''); }}
                                        disabled={level2Opts.length === 0}>
                                        <option value="">Todos</option>
                                        {level2Opts.map(c => <option key={c._id} value={c._id}>{c.name} ({c.questionsCount})</option>)}
                                    </select>
                                    <ChevronDown size={13} className="psc-select-icon" />
                                </div>
                            )}
                        </div>

                        <div className="psc-select-group">
                            <label className="psc-label">Tema</label>
                            {!level2Id ? (
                                <div className="psc-select-hint"><Lock size={12} /><span>Selecione um assunto</span></div>
                            ) : (
                                <div className="psc-select-wrap">
                                    <select className={`psc-select ${level3Id ? 'psc-select--active' : ''}`} value={level3Id}
                                        onChange={e => { setLevel3Id(e.target.value); setLevel4Id(''); }}
                                        disabled={level3Opts.length === 0}>
                                        <option value="">Todos</option>
                                        {level3Opts.map(c => <option key={c._id} value={c._id}>{c.name} ({c.questionsCount})</option>)}
                                    </select>
                                    <ChevronDown size={13} className="psc-select-icon" />
                                </div>
                            )}
                        </div>

                        <div className="psc-select-group">
                            <label className="psc-label">Subtema</label>
                            {!level3Id ? (
                                <div className="psc-select-hint"><Lock size={12} /><span>Selecione um tema</span></div>
                            ) : (
                                <div className="psc-select-wrap">
                                    <select className={`psc-select ${level4Id ? 'psc-select--active' : ''}`} value={level4Id}
                                        onChange={e => setLevel4Id(e.target.value)}
                                        disabled={level4Opts.length === 0}>
                                        <option value="">Todos</option>
                                        {level4Opts.map(c => <option key={c._id} value={c._id}>{c.name} ({c.questionsCount})</option>)}
                                    </select>
                                    <ChevronDown size={13} className="psc-select-icon" />
                                </div>
                            )}
                        </div>

                    </div>
                </div>

                {/* Dificuldade */}
                <div className="psc-group">
                    <div className="psc-section-label">Dificuldade</div>
                    <div className="psc-chips">
                        <button type="button" className={`psc-chip ${!difficulty ? 'psc-chip--active' : ''}`} onClick={() => setDifficulty('')}>
                            Todas
                        </button>
                        {DIFFICULTY_OPTIONS.map(opt => (
                            <button key={opt.value} type="button"
                                className={`psc-chip ${difficulty === opt.value ? 'psc-chip--active' : ''}`}
                                style={difficulty === opt.value ? { '--chip-color': opt.color } as React.CSSProperties : undefined}
                                onClick={() => setDifficulty(difficulty === opt.value ? '' : opt.value)}>
                                <span className="psc-chip-dot" style={{ background: opt.color }} />
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Quantidade */}
                <div className="psc-group">
                    <div className="psc-section-label">Quantidade de questões</div>
                    <input
                        type="number"
                        className="psc-input psc-input--qty"
                        min={1}
                        max={200}
                        value={qty}
                        onChange={e => setQty(Math.max(1, Math.min(200, Number(e.target.value))))}
                    />
                </div>

            </div>

            {/* ── Painel direito: resumo + botão ──────────────────── */}
            <div className="psc-summary-panel">
                <div className="psc-summary-header">
                    <FlaskConical size={16} />
                    <span>Resumo</span>
                </div>

                <div className="psc-summary-body">

                    {/* Nome */}
                    <div className="psc-summary-name">{summaryName}</div>

                    {/* Conteúdo */}
                    <div className="psc-summary-section">
                        <span className="psc-summary-section-label">Conteúdo</span>
                        {level1Id ? (
                            <div className="psc-summary-tags">
                                <span className="psc-summary-tag">{getName(level1Id)}</span>
                                {level2Id && <span className="psc-summary-tag">{getName(level2Id)}</span>}
                                {level3Id && <span className="psc-summary-tag">{getName(level3Id)}</span>}
                                {level4Id && <span className="psc-summary-tag">{getName(level4Id)}</span>}
                            </div>
                        ) : (
                            <span className="psc-summary-all">Banco completo</span>
                        )}
                    </div>

                    {/* Dificuldade */}
                    <div className="psc-summary-section">
                        <span className="psc-summary-section-label">Dificuldade</span>
                        <span className="psc-summary-tag" style={diffColor ? { color: diffColor, background: `${diffColor}18`, borderColor: `${diffColor}40` } : undefined}>
                            {diffLabel}
                        </span>
                    </div>

                    {/* Contagem */}
                    <div className="psc-summary-count-box">
                        {countLoading ? (
                            <div className="psc-summary-spinner" />
                        ) : questionCount !== null ? (
                            questionCount === 0 ? (
                                <span className="psc-summary-count-zero">Sem questões<br />com esses filtros</span>
                            ) : (
                                <>
                                    <span className="psc-summary-count-number">{effectiveQty}</span>
                                    <span className="psc-summary-count-label">
                                        questão{effectiveQty !== 1 ? 'ões' : ''}
                                        {questionCount < qty && (
                                            <span className="psc-summary-count-max"> · máx {questionCount}</span>
                                        )}
                                    </span>
                                </>
                            )
                        ) : (
                            <span className="psc-summary-count-idle">—</span>
                        )}
                    </div>

                </div>

                <button className="psc-create-btn" onClick={handleCreate} disabled={!canCreate} type="button">
                    {creating
                        ? <><div className="psc-btn-spinner" /> Criando…</>
                        : <><FlaskConical size={16} /> Criar simulado</>
                    }
                </button>
            </div>

        </div>
    );
};

// ── Histórico de Simulados ────────────────────────────────────
const SimuladosHistoricoView: React.FC<{
    simulados: Simulado[];
    loading: boolean;
    onDelete: (id: string) => void;
}> = ({ simulados, loading, onDelete }) => {
    const navigate = useNavigate();
    const formatDate = (d: string | null) => d ? new Date(d).toLocaleDateString('pt-BR') : '—';

    const statusConfig = {
        criado:       { label: 'Novo',         cls: 'sim-badge--new' },
        em_andamento: { label: 'Em andamento', cls: 'sim-badge--progress' },
        finalizado:   { label: 'Concluído',    cls: 'sim-badge--done' },
    };

    if (loading) return <div className="ps-loading"><div className="ps-spinner" /><p>Carregando simulados…</p></div>;

    if (simulados.length === 0) return (
        <div className="ps-empty">
            <FlaskConical size={56} />
            <h3>Nenhum simulado criado</h3>
            <p>Use "Criar simulado" para montar um simulado com seus filtros preferidos</p>
        </div>
    );

    return (
        <div className="sim__grid">
            {simulados.map(sim => {
                const { label, cls } = statusConfig[sim.status] ?? statusConfig.criado;
                return (
                    <div key={sim._id} className="sim__card">
                        <div className="sim__card-top">
                            <div className="sim__card-info">
                                <h3 className="sim__card-title">{sim.name}</h3>
                                <div className="sim__card-meta">
                                    <Calendar size={11} />
                                    <span>{formatDate(sim.createdAt)}</span>
                                    <span>·</span>
                                    <span>{sim.config.totalQuestions} questões</span>
                                    {sim.config.difficulty !== 'all' && <><span>·</span><span style={{ textTransform: 'capitalize' }}>{sim.config.difficulty}</span></>}
                                </div>
                            </div>
                            <span className={`sim__badge ${cls}`}>{label}</span>
                        </div>

                        {sim.status === 'finalizado' && sim.results && (
                            <div className="sim__results">
                                <span className="sim__results-accuracy">{sim.results.accuracy}%</span>
                                <span className="sim__results-detail">{sim.results.totalCorrect}/{sim.questionIds.length} acertos</span>
                                {sim.results.totalTime > 0 && (
                                    <span className="sim__results-time">
                                        <Clock size={11} /> {Math.floor(sim.results.totalTime / 60)}min
                                    </span>
                                )}
                            </div>
                        )}

                        <div className="sim__actions">
                            {sim.status !== 'finalizado' ? (
                                <button className="sim__btn sim__btn--primary" onClick={() => navigate(`/simulado/${sim._id}`)}>
                                    <Play size={14} />
                                    {sim.status === 'criado' ? 'Iniciar' : 'Continuar'}
                                </button>
                            ) : (
                                <button className="sim__btn sim__btn--view" onClick={() => navigate(`/simulado/${sim._id}`)}>
                                    <Eye size={14} /> Ver Resultado
                                </button>
                            )}
                            <button className="sim__btn sim__btn--delete" onClick={() => onDelete(sim._id)} title="Deletar simulado">
                                <Trash2 size={14} />
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// ═════════════════════════════════════════════════════════════
//  COMPONENTE PRINCIPAL
// ═════════════════════════════════════════════════════════════
const ProvasSimulados: React.FC<ProvasSimuladosProps> = ({ onShowToast, onSectionChange }) => {
    const [section, setSection] = useState<SectionState>('provas-catalogo');

    const [exams,         setExams]         = useState<OfficialExam[]>([]);
    const [attempts,      setAttempts]      = useState<UserExamAttempt[]>([]);
    const [loadingProvas, setLoadingProvas] = useState(false);

    const [simulados,        setSimulados]        = useState<Simulado[]>([]);
    const [loadingSimulados, setLoadingSimulados] = useState(false);

    const changeSection = useCallback((s: SectionState) => {
        setSection(s);
        onSectionChange(s);
    }, [onSectionChange]);

    const loadProvasData = useCallback(async () => {
        try {
            setLoadingProvas(true);
            const [examsData, attemptsData] = await Promise.all([
                examService.getExams(),
                examService.getMyAttempts(),
            ]);
            setExams(examsData);
            setAttempts(attemptsData);
        } catch { onShowToast('Erro ao carregar provas', 'error'); }
        finally { setLoadingProvas(false); }
    }, [onShowToast]);

    const loadSimuladosData = useCallback(async () => {
        try {
            setLoadingSimulados(true);
            const data = await simuladoService.getSimulados();
            setSimulados(data);
        } catch { onShowToast('Erro ao carregar simulados', 'error'); }
        finally { setLoadingSimulados(false); }
    }, [onShowToast]);

    // Carrega provas na montagem (default = provas-catalogo)
    useEffect(() => {
        loadProvasData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Carrega simulados quando navegar para aquela seção
    useEffect(() => {
        if (section === 'simulados-historico') loadSimuladosData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [section]);

    const handleDeleteAttempt = async (attemptId: string) => {
        if (!window.confirm('Remover esta tentativa do histórico?')) return;
        try {
            await examService.deleteAttempt(attemptId);
            onShowToast('Removido do histórico', 'success');
            setAttempts(prev => prev.filter(a => a._id !== attemptId));
        } catch { onShowToast('Erro ao remover', 'error'); }
    };

    const handleDeleteSimulado = async (id: string) => {
        if (!window.confirm('Deletar este simulado?')) return;
        try {
            await simuladoService.deleteSimulado(id);
            onShowToast('Simulado deletado', 'success');
            setSimulados(prev => prev.filter(s => s._id !== id));
        } catch { onShowToast('Erro ao deletar simulado', 'error'); }
    };

    const finishedAttempts = attempts.filter(a => a.status === 'finalizado');
    const isProvasActive   = section.startsWith('provas-');
    const isSimuladosActive = section.startsWith('simulados-');

    return (
        <div className="ps-page">

            {/* ── Cards permanentes de domínio ───────────────────── */}
            <div className="psm-header">
                <div className="psm-cards">

                    <div className={`psm-card psm-card--provas${isProvasActive ? ' psm-card--active' : ''}`}>
                        <div className="psm-card-icon"><Award size={24} /></div>
                        <div className="psm-card-body">
                            <h3 className="psm-card-title">Provas</h3>
                            <p className="psm-card-sub">Provas do ENEM e vestibulares</p>
                        </div>
                        <div className="psm-card-actions">
                            <button
                                type="button"
                                className={`psm-btn psm-btn--provas${section === 'provas-catalogo' ? ' psm-btn--active' : ''}`}
                                onClick={() => changeSection('provas-catalogo')}
                            >
                                Catálogo
                            </button>
                            <button
                                type="button"
                                className={`psm-btn psm-btn--provas${section === 'provas-historico' ? ' psm-btn--active' : ''}`}
                                onClick={() => changeSection('provas-historico')}
                            >
                                Histórico
                            </button>
                        </div>
                    </div>

                    <div className={`psm-card psm-card--simulados${isSimuladosActive ? ' psm-card--active' : ''}`}>
                        <div className="psm-card-icon"><SlidersHorizontal size={24} /></div>
                        <div className="psm-card-body">
                            <h3 className="psm-card-title">Simulados</h3>
                            <p className="psm-card-sub">Simulados personalizados por assunto</p>
                        </div>
                        <div className="psm-card-actions">
                            <button
                                type="button"
                                className={`psm-btn psm-btn--simulados${section === 'simulados-criar' ? ' psm-btn--active' : ''}`}
                                onClick={() => changeSection('simulados-criar')}
                            >
                                Criar simulado
                            </button>
                            <button
                                type="button"
                                className={`psm-btn psm-btn--simulados${section === 'simulados-historico' ? ' psm-btn--active' : ''}`}
                                onClick={() => changeSection('simulados-historico')}
                            >
                                Histórico
                            </button>
                        </div>
                    </div>

                </div>
            </div>

            {/* ── Área de conteúdo (troca com a seção ativa) ────────── */}
            <div className="ps-content-area">
                {section === 'provas-catalogo' && (
                    <ProvasCatalogView exams={exams} attempts={attempts} loading={loadingProvas} />
                )}
                {section === 'provas-historico' && (
                    <ProvasHistoricoView
                        finishedAttempts={finishedAttempts}
                        exams={exams}
                        loading={loadingProvas}
                        onDeleteAttempt={handleDeleteAttempt}
                    />
                )}
                {section === 'simulados-criar' && (
                    <SimuladosCriarView onShowToast={onShowToast} />
                )}
                {section === 'simulados-historico' && (
                    <SimuladosHistoricoView
                        simulados={simulados}
                        loading={loadingSimulados}
                        onDelete={handleDeleteSimulado}
                    />
                )}
            </div>

        </div>
    );
};

export default ProvasSimulados;
